import { useState } from "react";
import { Spinner } from "@/components/ui/spinner";
import { CheckIcon, ExternalLinkIcon } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import { useAuth } from "@clerk/react";
import { CreatorAction } from "@/types/types";
import { currencySymbol } from "@/lib/format";
import { Button } from "@/components/ui/button";
import {
  Frame,
  FrameHeader,
  FramePanel,
  FrameTitle,
} from "@/components/ui/frame";
import { useDashboardStore } from "@/stores";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

/**
 * Performance-CPM payment task: the analytics cron measured the creator's
 * tracked videos after the agreed window and computed the payout from the
 * FROZEN deal terms (campaign_creators.performance_terms). This card shows
 * the math of record; "Mark as handled" clears the row once the team has
 * paid the creator (payment itself happens however the org pays).
 */
export default function PerformancePaymentAction({ action }: { action: CreatorAction }) {
  const { orgId, getToken } = useAuth();
  const [resolving, setResolving] = useState(false);

  const payment = action.performance_payment;
  const terms = action.performance_terms;
  const symbol = currencySymbol(action.currency);
  const fmt = (n: number) => n.toLocaleString("en-US");

  const computedAt = payment?.computed_at
    ? format(new Date(payment.computed_at), "MMM d, yyyy")
    : null;

  const markHandled = async () => {
    if (!orgId) return;
    setResolving(true);
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE_URL}/api/campaigns/resolvePerformancePayment`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          campaign_creator_id: action._id,
          organization_id: orgId,
        }),
      });
      const json = await res.json();
      if (json.success) {
        toast.success("Performance payment marked as handled");
        useDashboardStore.setState((state) => ({
          tasks: state.tasks.filter((task) => task._id !== action._id),
        }));
      } else {
        toast.error(json.error ?? "Failed to mark as handled");
      }
    } catch (err) {
      toast.error(`Failed: ${err instanceof Error ? err.message : "Unknown error"}`);
    } finally {
      setResolving(false);
    }
  };

  if (!payment) return null;

  return (
    <div className="flex flex-col items-start gap-4 animate-in fade-in-0 slide-in-from-top-2 duration-250">
      <p className="text-sm text-muted-foreground">
        {action.message ?? `A performance payment for @${action.username} is due.`}
        {computedAt && ` Measured ${computedAt}.`}
        {payment.capped && " The bracket total exceeded the agreed cap — the capped amount is owed."}
      </p>

      <Frame className="w-full">
        <FrameHeader>
          <FrameTitle>The math</FrameTitle>
        </FrameHeader>
        <FramePanel className="p-0 overflow-hidden">
          <div className="divide-y text-sm">
            {payment.breakdown.map((row, i) => (
              <div key={i} className="flex items-center justify-between gap-3 px-3 py-2">
                <a
                  href={row.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex min-w-0 items-center gap-1 text-primary hover:underline"
                >
                  <span className="truncate">{row.link}</span>
                  <ExternalLinkIcon className="size-3.5 shrink-0 opacity-60" />
                </a>
                <span className="shrink-0 text-muted-foreground">{fmt(row.views)} views</span>
                <span className="shrink-0 font-medium">
                  {symbol}
                  {fmt(row.amount)}
                </span>
              </div>
            ))}
            <div className="flex items-center justify-between gap-3 px-3 py-2 font-medium">
              <span>Total{payment.capped ? " (capped)" : ""}</span>
              <span className="text-muted-foreground">{fmt(payment.views_total)} views</span>
              <span>
                {symbol}
                {fmt(payment.amount)}
              </span>
            </div>
          </div>
        </FramePanel>
      </Frame>

      {terms?.rates?.length ? (
        <Frame className="w-full">
          <FrameHeader>
            <FrameTitle>Agreed terms</FrameTitle>
          </FrameHeader>
          <FramePanel className="p-3 text-sm text-muted-foreground space-y-1">
            {terms.rates.map((rate, i) => (
              <div key={i}>
                {symbol}
                {fmt(rate.cpm)} per 1,000 views above {fmt(rate.above_views)}
              </div>
            ))}
            <div>
              Capped at {symbol}
              {fmt(terms.cap)} · measured {terms.window_months} month
              {terms.window_months === 1 ? "" : "s"} after posting
              {terms.median_used != null && ` · thresholds set from a median of ${fmt(terms.median_used)} views`}
            </div>
          </FramePanel>
        </Frame>
      ) : null}

      <Frame className="w-full">
        <FrameHeader>
          <FrameTitle>Decision</FrameTitle>
        </FrameHeader>
        <FramePanel className="flex w-full items-center justify-between gap-2 p-3">
          <span className="text-sm text-muted-foreground">
            Pay the creator, then mark this task as handled.
          </span>
          <Button onClick={markHandled} disabled={resolving}>
            {resolving ? <Spinner /> : <CheckIcon />}
            Mark as handled
          </Button>
        </FramePanel>
      </Frame>
    </div>
  );
}
